import { Component } from 'react';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import { withRouter } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import { ButtonReset, MAIN_HEADER_HEIGHT, MAIN_SIDEBAR_WIDTH, media, size, spacing, ui } from 'ui';
import Avatar from 'components/Avatar';
import Dropdown from 'components/Dropdown';
import { PopoverItem, PopoverItemButton, PopoverItemLink, PopoverMenu } from 'ui/popover';
import getAttachmentURL from 'utils/getAttachmentURL';
import { MdArrowDropDown } from 'react-icons/md';
import IcNotification from 'components/icons/Notification';
import { resetImpersonatedUser } from 'actions/user';
import get from 'lodash/get';
import Feed from 'containers/Feed';
import { getSelectedPlace } from 'selectors';
import { getPlaceNotificationsCount } from 'actions/places';
import { withTranslation } from 'react-i18next';
import { toggleOnlyShowWorkOrders } from 'actions/ui';
import { Stack } from '@tymate/margaret';
import CommunicationBanner from './CommunicationBanner';
import { capitalize } from '@tymate/sergic';

const Wrapper = styled.header`
   right: 0;
   height: ${props => !props.isActiveMaintenance && MAIN_HEADER_HEIGHT};
   z-index: 3000;
   align-items: center;
   justify-content: flex-end;
   padding: 0 ${spacing()};
   background-color: #fff;
   box-shadow: inset 0 -1px 0 ${({ theme }) => theme.separator};
   left: 0;
   position: relative;
   top: 0;
   display: none;

   ${media.tablet`
    display: flex;
    position: fixed;
    left: ${MAIN_SIDEBAR_WIDTH};
  `};
`;

const AccountTriggerWrapper = styled.span`
   display: flex;
   align-items: center;
   padding: 6px ${spacing(0.5)};
   box-shadow: inset 0 0 0 1px ${({ theme }) => theme.separator};
   border-radius: 4px;

   &:hover {
      box-shadow: inset 0 0 0 1px ${({ theme }) => theme.separator}, 0 2px 4px rgba(0, 0, 0, 0.12);
   }
`;

const AccountName = styled.span`
   display: inline-block;
   font-size: ${size(-1)};
   font-weight: 500;
`;

const DropdownContainer = styled.div`
   height: 100%;
   display: flex;
   align-items: flex-start;
   justify-content: flex-end;
   margin-top: 1rem;

   > * + * {
      margin-left: ${({ theme }) => theme.spacing()};
   }
`;

const WrapperCaret = styled.div`
   display: flex;
   align-items: center;
   margin-right: ${spacing(0.5)};
   margin-left: ${spacing(0.5)};
   font-size: 1.5em;
   color: ${({ theme }) => theme.textLight};
`;

const NotificationContainer = styled.span`
   position: relative;
`;

const NotificationBadge = styled.span`
   position: absolute;
   background-color: ${ui('error')};
   color: #fff;
   width: 1.5em;
   height: 1.5em;
   font-size: ${size(-3)};
   display: flex;
   align-items: center;
   justify-content: center;
   border-radius: 2em;
   overflow: hidden;
   white-space: nowrap;
   text-overflow: ellipsis;
   right: 0;
   top: 0;
   transform: translate3d(50%, -50%, 0);

   ${props =>
      props.size === 'medium' &&
      css`
         width: 1.75em;
         height: 1.75em;
      `} ${props =>
      props.size === 'big' &&
      css`
         width: 2em;
         height: 2em;
      `};
`;

const Banner = styled(Stack)`
   width: 80%;
`;

class Header extends Component {
   componentDidUpdate(prevProps) {
      if (this.props.location.key !== prevProps.location.key) {
         this.dropdown.close();
         if (this.feedDropdown) {
            this.feedDropdown.close();
         }
      }
   }

   componentDidMount() {
      this.poll = setInterval(this.fetchData, 60000);

      if (get(this.props, 'location.state.highlightedWorkOrder')) {
         setTimeout(() => {
            if (this.feedDropdown) {
               this.feedDropdown.open();
            }
            this.props.toggleOnlyShowWorkOrders();
         });
      }
   }

   fetchData = () => {
      const { placeId, getPlaceNotificationsCount } = this.props;

      if (!placeId) {
         return;
      }

      getPlaceNotificationsCount(placeId);
   };

   handleSwitchUser = () => {
      const { resetImpersonatedUser } = this.props;
      resetImpersonatedUser();
   };

   componentWillUnmount() {
      clearInterval(this.poll);
   }

   render() {
      const { currentUser, onLogout, isImpersonating, place, t, maintenanceData } = this.props;
      const currentIdentity = get(currentUser, 'currentIdentity', {});
      const { firstName, lastName, displayName } = currentIdentity;
      const avatarURL = null && getAttachmentURL(currentIdentity.avatarUrl);
      return (
         <Wrapper isActiveMaintenance={maintenanceData?.is_active}>
            <Banner>
               <CommunicationBanner maintenanceData={maintenanceData} />
            </Banner>
            <DropdownContainer>
               <Dropdown
                  ref={c => (this.feedDropdown = c)}
                  right
                  size='big'
                  trigger={
                     <NotificationContainer>
                        <IcNotification size={24} />
                        {Boolean(place.unreadFeedEventCount) && (
                           <NotificationBadge
                              size={
                                 place.unreadFeedEventCount > 99
                                    ? 'big'
                                    : place.unreadFeedEventCount > 9
                                    ? 'medium'
                                    : null
                              }
                           >
                              {place.unreadFeedEventCount}
                           </NotificationBadge>
                        )}
                     </NotificationContainer>
                  }
               >
                  <PopoverMenu variant='bare' style={{ maxHeight: 480, overflowY: 'auto' }}>
                     <Switch>
                        <Route component={Feed} />
                     </Switch>
                  </PopoverMenu>
               </Dropdown>

               <Dropdown
                  ref={c => (this.dropdown = c)}
                  right
                  trigger={
                     <AccountTriggerWrapper>
                        <AccountName>{capitalize(displayName)}</AccountName>
                        <WrapperCaret>
                           <MdArrowDropDown />
                        </WrapperCaret>
                        <Avatar small firstName={firstName} lastName={lastName} avatarURL={avatarURL} />
                     </AccountTriggerWrapper>
                  }
               >
                  <PopoverMenu>
                     <PopoverItem>
                        <PopoverItemLink to='/parametres/mon-profil'>{t('header:profile')}</PopoverItemLink>
                     </PopoverItem>

                     {isImpersonating && (
                        <PopoverItem>
                           <PopoverItemLink as={ButtonReset} onClick={this.handleSwitchUser}>
                              {t('header:changeUser')}
                           </PopoverItemLink>
                        </PopoverItem>
                     )}

                     <PopoverItem>
                        <PopoverItemButton onClick={onLogout}>{t('header:logout')}</PopoverItemButton>
                     </PopoverItem>
                  </PopoverMenu>
               </Dropdown>
            </DropdownContainer>
         </Wrapper>
      );
   }
}

const mapStateToProps = state => ({
   isImpersonating: Boolean(state.user.impersonatedUser),
   place: getSelectedPlace(state),
   placeId: state.ui.selectedPlaceId,
   maintenanceData: state.communication.data,
});

const mapDispatchToProps = dispatch => ({
   resetImpersonatedUser: () => dispatch(resetImpersonatedUser()),
   getPlaceNotificationsCount: id => dispatch(getPlaceNotificationsCount(id)),
   toggleOnlyShowWorkOrders: () => dispatch(toggleOnlyShowWorkOrders()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withTranslation()(Header)));
